import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '@/utils/classnames';
import { MaterialIcon } from '@/components/MaterialIcon';

export interface SidebarNavItem {
  to: string;
  label: string;
  icon: string;
  group?: string;
  /** When true, NavLink uses `end` to require an exact match. */
  exact?: boolean;
}

export const SIDEBAR_ITEMS: readonly SidebarNavItem[] = [
  { to: '/', label: '总览', icon: 'dashboard', exact: true },
  { to: '/manifest', label: 'Access Manifest', icon: 'rule' },
  { to: '/ingress', label: 'Ingress Plan', icon: 'dns' },
  { to: '/oidc', label: 'OIDC Provider', icon: 'key' },
  { to: '/audit', label: 'Audit Lite', icon: 'receipt_long' },
  { to: '/runtime', label: 'Runtime Config', icon: 'settings' },
  { to: '/ops/feishu-events', label: 'Feishu Events', icon: 'webhook', group: 'R3b Ops' },
  { to: '/ops/longconn', label: 'LongConn', icon: 'lan', group: 'R3b Ops' },
  { to: '/ops/group-polling', label: 'Group Polling', icon: 'sync', group: 'R3b Ops' },
  { to: '/ops/bindings', label: 'Bindings', icon: 'link', group: 'R3b Ops' },
  { to: '/ops/principals', label: 'Principals', icon: 'group', group: 'R3b Ops' },
  { to: '/ops/metrics', label: 'Metrics', icon: 'monitoring', group: 'R3b Ops' },
  { to: '/ops/authz', label: 'AuthZ', icon: 'policy', group: 'AuthZ' },
];

export interface SidebarProps {
  /**
   * `expanded` shows label + icon (≥1024px desktop default).
   * `collapsed` shows just the icon column (640-1024px tablet).
   * The mobile (<640px) breakpoint hides the sidebar entirely; AppShell
   * renders a bottom tab bar instead.
   */
  variant?: 'expanded' | 'collapsed';
}

export function Sidebar({ variant = 'expanded' }: SidebarProps) {
  const location = useLocation();
  const collapsed = variant === 'collapsed';
  return (
    <nav
      aria-label="主导航"
      className={cn(
        'fixed left-0 top-0 z-40 flex h-screen flex-col bg-surface border-r border-outline-variant',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <div
        className={cn(
          'flex flex-col gap-stack-sm border-b border-outline-variant',
          collapsed ? 'items-center px-stack-sm py-stack-md' : 'p-container-padding',
        )}
      >
        {collapsed ? (
          <span className="font-headline-md text-headline-md text-primary">D</span>
        ) : (
          <>
            <h1 className="font-headline-md text-headline-md text-primary">Denox Identity</h1>
            <span className="font-label-caps text-label-caps uppercase text-on-surface-variant">
              Fabric Console
            </span>
          </>
        )}
      </div>

      <ul className="mt-stack-sm flex flex-col gap-unit overflow-y-auto pb-stack-md">
        {SIDEBAR_ITEMS.map((item, index) => {
          const active =
            item.exact === true
              ? location.pathname === item.to
              : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
          return (
            <li key={item.to} className="relative px-stack-sm">
              {!collapsed && item.group && SIDEBAR_ITEMS.findIndex((x) => x.group === item.group) === index ? (
                <div className="px-stack-md pb-unit pt-stack-sm font-label-caps text-label-caps uppercase text-outline">
                  {item.group}
                </div>
              ) : null}
              {active ? (
                <motion.span
                  layoutId="nav-active"
                  className="absolute inset-y-0 left-0 my-unit w-1 rounded-r-lg bg-primary"
                  transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                  aria-hidden
                />
              ) : null}
              <NavLink
                to={item.to}
                end={item.exact}
                title={collapsed ? item.label : undefined}
                className={cn(
                  'group flex items-center rounded px-stack-md py-stack-sm transition-colors duration-150 ease-in-out',
                  collapsed ? 'justify-center gap-0' : 'gap-stack-sm',
                  active
                    ? 'bg-surface-container-high text-primary'
                    : 'text-on-surface-variant hover:bg-surface-container-high',
                )}
              >
                <MaterialIcon name={item.icon} size={20} />
                {collapsed ? null : (
                  <span className="font-body-md text-body-md">{item.label}</span>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
